import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import CollapsibleHeader from '@layouts/collapsHeader/componenets/CollapsHeader';
import CompanyLocationDropDowns from '@shared/components/CompanyLocations.jsx';
import DeleteModal from '@shared/components/DeleteModal';
import ActionButtons from '@shared/components/ActionButtons';
import { COMMON_BUTTONS } from '@config/actionButtonConfigs.js';
import { companiesService } from '@shared/services/companiesService';
import { notifyError, notifySuccess } from '@config/tostifyConfig/tostifyConfig.js';
import { useForm } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

export default function Companies() {
    const [showMainData, setShowMainData] = useState(true);
    const [showBankAccounts, setShowBankAccounts] = useState(true);
    const [showCompanyBranch, setShowCompanyBranch] = useState(true);
    const [showSigners, setShowSigners] = useState(true);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [companiesData, setCompaniesData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isCreating, setIsCreating] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [nationalRevenue, setNationalRevenue] = useState([]);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const validationSchema = yup.object().shape({
        branchNo: yup.string().required(t('common.required')),
        name: yup.string().required(t('common.required')),
        bulstat: yup.string().required(t('common.required')),
        city: yup.string().required(t('common.required')),
        postcode: yup.string().required(t('common.required'))
    });

    const { register, setValue, watch, handleSubmit, formState: { errors }, reset } = useForm({
        resolver: yupResolver(validationSchema),
        mode: 'onChange',
        reValidateMode: 'onChange',
        criteriaMode: 'all'
    });

    // Combined form data state
    const [formData, setFormData] = useState({
        // Main data
        branchNo: '',
        name: '',
        bulstat: '',
        city: '',
        address: '',
        phone: '',
        postcode: '',
        status: 1,
        aliasBranchNo: '',
        tdNra: '',
        ibanTdNra: '',
        tdNraId: '',
        regionId: '',
        subregionId: '',
        cityId: '',
        mobilephone: '',
        email: '',
        nkidCode: '',
        nkidLevel1: '',
        nkidLevel2: '',
        nkidLevel3: '',
        formedPaymentDocs: 0,
        formedAccOperations: 0,
        isWorkingIdm: 0,
        isWorkingDwh: 1,
        subordination: 1,
        // Bank accounts
        firmBankAccByFirmaccId: '',
        firmBankAccBySharesFirmaccId: '',
        firmBankAccByDividendFirmaccId: '',
        firmBankAccByCreditTrnsfFirmaccId: '',
        // Signers data
        signers: {
            manager: { position: '', name: '' },
            hrManager: { position: '', name: '' },
            payrollManager: { position: '', name: '' },
            chiefAccountant: { position: '', name: '' },
            hrChiefExpert: { position: '', name: '' },
            payrollChiefExpert: { position: '', name: '' },
            cashier: { position: '', name: '' }
        }
    });

    // NKID dropdown options state
    const [nkidDropdownOptions, setNkidDropdownOptions] = useState({
        level1: { options: [], loading: false },
        level2: { options: [], loading: false },
        level3: { options: [], loading: false }
    });

    // Watch location fields
    const country = watch('country');
    const region = watch('region');
    const subregion = watch('subregion');
    const cityId = watch('cityId');

    // Update formData when location fields change
    useEffect(() => {
        if (isCreating) {
            setFormData(prev => ({
                ...prev,
                regionId: region || '',
                subregionId: subregion || '',
                cityId: cityId || ''
            }));
        }
    }, [region, subregion, cityId, isCreating]);

    // Fetch all data when component mounts
    useEffect(() => {
        // Only fetch if we're not returning from bank account selection
        if (!location.state?.preservedState) {
            fetchCompanies();
            fetchNationalRevenue();
            fetchNkidLevel1Options();
        } else {
            console.error('⏸️ Preserved state detected - Skipping initial fetch');
        }
    }, []);

    // Handle bank account selection from navigation state
    useEffect(() => {
        const handleBankAccountReturn = async () => {
            if (location.state?.selectedBankAccount && location.state?.fieldName && location.state?.preservedState) {
                const { selectedBankAccount, fieldName, preservedState } = location.state;
                // Fetch companies list but don't auto-select
                try {
                    const companies = await companiesService.getAllCompanies();
                    setCompaniesData(companies);
                } catch (error) {
                    console.error('❌ Error fetching companies:', error);
                }

                // Fetch national revenue data
                try {
                    const nationalRevenue = await companiesService.getNationalRevenue(9);
                    setNationalRevenue(nationalRevenue);
                } catch (error) {
                    console.error('❌ Error fetching national revenue:', error);
                }

                setLoading(false);
                setSelectedCompany(preservedState.selectedCompany);
                setFormData(preservedState.formData);
                setIsCreating(preservedState.isCreating);
                setNkidDropdownOptions(preservedState.nkidDropdownOptions);
                setShowMainData(preservedState.showMainData);
                setShowBankAccounts(preservedState.showBankAccounts);
                setShowCompanyBranch(preservedState.showCompanyBranch);
                setShowSigners(preservedState.showSigners);

                // Refetch bank accounts to get updated list (in case new one was added)
                if (preservedState.selectedCompany?.id) {
                    try {
                        const bankAccountsData = await companiesService.getBankAccounts(preservedState.selectedCompany.id);
                        setBankAccounts(Array.isArray(bankAccountsData) ? bankAccountsData : []);
                    } catch (error) {
                        console.error('❌ Error fetching bank accounts:', error);
                        // Fall back to preserved bank accounts if fetch fails
                        setBankAccounts(preservedState.bankAccounts);
                    }
                } else {
                    setBankAccounts(preservedState.bankAccounts);
                }

                // Update the field with selected bank account
                setTimeout(() => {
                    handleInputChange(fieldName, selectedBankAccount.id.toString());
                }, 100);

                navigate(location.pathname, { replace: true, state: {} });
            }
        };

        handleBankAccountReturn();
    }, [location.state]);

    // Fetch NKID Level 1 options
    const fetchNkidLevel1Options = async () => {
        try {
            setNkidDropdownOptions(prev => ({
                ...prev,
                level1: { ...prev.level1, loading: true }
            }));

            const data = await companiesService.getRootBusinessQualification();
            const options = data.map(item => ({
                value: item.value,
                label: `${item.value} - ${item.note}`
            }));

            setNkidDropdownOptions(prev => ({
                ...prev,
                level1: { options, loading: false }
            }));
        } catch (error) {
            console.error('Error fetching NKID level 1 options:', error);
            setNkidDropdownOptions(prev => ({
                ...prev,
                level1: { options: [], loading: false }
            }));
        }
    };

    const fetchCompanies = async () => {
        try {
            setLoading(true);
            const companies = await companiesService.getAllCompanies();
            setCompaniesData(companies);

            // Auto-select the first company if there are companies available
            if (companies && companies.length > 0) {
                handleCompanySelect(companies[0]);
            }
        } catch (error) {
            console.error('❌ Error fetching companies:', error);
            notifyError(t('organizationalStructure.companyBranch.fetchError'));
        } finally {
            setLoading(false);
        }
    };

    const fetchNationalRevenue = async () => {
        const nationalRevenue = await companiesService.getNationalRevenue(9);
        setNationalRevenue(nationalRevenue);
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        setValue(field, value, { shouldValidate: true });
    };

    const handleSignersInputChange = (role, field, value) => {
        setFormData(prev => ({
            ...prev,
            signers: {
                ...prev.signers,
                [role]: {
                    ...prev.signers[role],
                    [field]: value
                }
            }
        }));
    };

    const handleCityChange = (postalCode) => {
        if (postalCode) {
            handleInputChange('postcode', postalCode);
        }
    };

    const clearForm = (shouldSetCreating = true) => {
        setFormData({
            branchNo: '',
            name: '',
            bulstat: '',
            city: '',
            address: '',
            phone: '',
            postcode: '',
            status: 1,
            aliasBranchNo: '',
            tdNra: '',
            ibanTdNra: '',
            tdNraId: '',
            regionId: '',
            subregionId: '',
            cityId: '',
            mobilephone: '',
            email: '',
            nkidCode: '',
            nkidLevel1: '',
            nkidLevel2: '',
            nkidLevel3: '',
            formedPaymentDocs: 0,
            formedAccOperations: 0,
            isWorkingIdm: 0,
            isWorkingDwh: 1,
            subordination: 1,
            firmBankAccByFirmaccId: '',
            firmBankAccBySharesFirmaccId: '',
            firmBankAccByDividendFirmaccId: '',
            firmBankAccByCreditTrnsfFirmaccId: '',
            signers: {
                manager: { position: '', name: '' },
                hrManager: { position: '', name: '' },
                payrollManager: { position: '', name: '' },
                chiefAccountant: { position: '', name: '' },
                hrChiefExpert: { position: '', name: '' },
                payrollChiefExpert: { position: '', name: '' },
                cashier: { position: '', name: '' }
            }
        });

        setValue('country', null);
        setValue('region', null);
        setValue('subregion', null);
        setValue('cityId', null);

        setSelectedCompany(null);

        if (shouldSetCreating) {
            setIsCreating(true);
        }

        // Reset NKID dropdown options for level 2 and 3
        setNkidDropdownOptions(prev => ({
            ...prev,
            level2: { options: [], loading: false },
            level3: { options: [], loading: false }
        }));
        // Reset react-hook-form
        reset({
            branchNo: '',
            name: '',
            bulstat: '',
            city: '',
            postcode: ''
        });
    };

    // Handle NKID Level 1 change
    const handleNkidLevel1Change = async (value) => {
        setFormData(prev => ({
            ...prev,
            nkidLevel1: value,
            nkidLevel2: '',
            nkidLevel3: '',
            nkidCode: ''
        }));

        // Clear child dropdowns
        setNkidDropdownOptions(prev => ({
            ...prev,
            level2: { options: [], loading: false },
            level3: { options: [], loading: false }
        }));

        // If a value is selected, fetch level 2 options
        if (value) {
            try {
                setNkidDropdownOptions(prev => ({
                    ...prev,
                    level2: { ...prev.level2, loading: true }
                }));

                const data = await companiesService.getBusinessQualification(value);
                const options = data.map(item => ({
                    value: item.value,
                    label: `${item.value} - ${item.note}`
                }));

                setNkidDropdownOptions(prev => ({
                    ...prev,
                    level2: { options, loading: false }
                }));
            } catch (error) {
                console.error('Error loading NKID level 2 options:', error);
                setNkidDropdownOptions(prev => ({
                    ...prev,
                    level2: { options: [], loading: false }
                }));
            }
        }
    };

    // Handle NKID Level 2 change
    const handleNkidLevel2Change = async (value) => {
        setFormData(prev => ({
            ...prev,
            nkidLevel2: value,
            nkidLevel3: '',
            nkidCode: ''
        }));

        // Clear child dropdown
        setNkidDropdownOptions(prev => ({
            ...prev,
            level3: { options: [], loading: false }
        }));

        // If a value is selected, fetch level 3 options
        if (value) {
            try {
                setNkidDropdownOptions(prev => ({
                    ...prev,
                    level3: { ...prev.level3, loading: true }
                }));

                const data = await companiesService.getBusinessQualification(value);
                const options = data.map(item => ({
                    value: item.value,
                    label: `${item.value} - ${item.note}`
                }));

                setNkidDropdownOptions(prev => ({
                    ...prev,
                    level3: { options, loading: false }
                }));
            } catch (error) {
                console.error('Error loading NKID level 3 options:', error);
                setNkidDropdownOptions(prev => ({
                    ...prev,
                    level3: { options: [], loading: false }
                }));
            }
        }
    };

    // Handle NKID Level 3 change
    const handleNkidLevel3Change = (value) => {
        setFormData(prev => ({
            ...prev,
            nkidLevel3: value,
            nkidCode: value // The final level 3 value is stored as nkidCode
        }));
        handleInputChange('nkidCode', value);
    };

    const onSubmit = async (data) => {
        try {
            if (!selectedCompany?.id && !isCreating) {
                notifyError(t('organizationalStructure.companyBranch.selectCompanyError'));
                return;
            }

            // Format signers data — only include roles with at least one filled field
            const formattedSigners = [
                {
                    modulId: "Hr",
                    mnemonicId: "director",
                    mnemonicDescription: formData.signers.manager.position,
                    qualityDescription: formData.signers.manager.name,
                    empBranchId: selectedCompany?.id,
                    nr: 1
                },
                {
                    modulId: "Hr",
                    mnemonicId: "hrManager",
                    mnemonicDescription: formData.signers.hrManager.position,
                    qualityDescription: formData.signers.hrManager.name,
                    empBranchId: selectedCompany?.id,
                    nr: 2
                },
                {
                    modulId: "Hr",
                    mnemonicId: "payrollManager",
                    mnemonicDescription: formData.signers.payrollManager.position,
                    qualityDescription: formData.signers.payrollManager.name,
                    empBranchId: selectedCompany?.id,
                    nr: 3
                },
                {
                    modulId: "Hr",
                    mnemonicId: "chiefAccountant",
                    mnemonicDescription: formData.signers.chiefAccountant.position,
                    qualityDescription: formData.signers.chiefAccountant.name,
                    empBranchId: selectedCompany?.id,
                    nr: 4
                },
                {
                    modulId: "Hr",
                    mnemonicId: "hrChiefExpert",
                    mnemonicDescription: formData.signers.hrChiefExpert.position,
                    qualityDescription: formData.signers.hrChiefExpert.name,
                    empBranchId: selectedCompany?.id,
                    nr: 5
                },
                {
                    modulId: "Hr",
                    mnemonicId: "payrollChiefExpert",
                    mnemonicDescription: formData.signers.payrollChiefExpert.position,
                    qualityDescription: formData.signers.payrollChiefExpert.name,
                    empBranchId: selectedCompany?.id,
                    nr: 6
                },
                {
                    modulId: "Hr",
                    mnemonicId: "cashier",
                    mnemonicDescription: formData.signers.cashier.position,
                    qualityDescription: formData.signers.cashier.name,
                    empBranchId: selectedCompany?.id,
                    nr: 7
                }
            ].filter(
                (signer) => signer.mnemonicDescription?.trim() || signer.qualityDescription?.trim()
            );

            // Prepare company data
            const submitData = {
                branchNo: data.branchNo,
                name: data.name,
                bulstat: data.bulstat,
                city: data.city,
                postcode: data.postcode,
                address: formData.address,
                phone: formData.phone,
                status: formData.status,
                aliasBranchNo: formData.aliasBranchNo,
                tdNra: formData.tdNra,
                ibanTdNra: formData.ibanTdNra,
                tdNraId: formData.tdNraId,
                regionId: formData.regionId,
                subregionId: formData.subregionId,
                cityId: typeof formData.cityId === 'object' ? formData.cityId.id : formData.cityId,
                mobilephone: formData.mobilephone,
                email: formData.email,
                nkidCode: formData.nkidCode,
                formedPaymentDocs: formData.formedPaymentDocs,
                formedAccOperations: formData.formedAccOperations,
                isWorkingIdm: formData.isWorkingIdm || 0,
                isWorkingDwh: formData.isWorkingDwh || 1,
                subordination: formData.subordination || 1,
                firmBankAccByFirmaccId: formData.firmBankAccByFirmaccId,
                firmBankAccBySharesFirmaccId: formData.firmBankAccBySharesFirmaccId,
                firmBankAccByDividendFirmaccId: formData.firmBankAccByDividendFirmaccId,
                firmBankAccByCreditTrnsfFirmaccId: formData.firmBankAccByCreditTrnsfFirmaccId
            };

            let companyId = selectedCompany?.id;

            // Update or create company
            if (companyId) {
                // When editing, update company and signers (only if any signer has data)
                const updatePromises = [companiesService.updateCompany(companyId, submitData)];
                if (formattedSigners.length > 0) {
                    updatePromises.push(companiesService.updateSigners(formattedSigners));
                }
                await Promise.all(updatePromises);
                notifySuccess(t('organizationalStructure.companyBranch.updateSuccess'));
            } else {
                // When creating, only create the company
                const newCompany = await companiesService.addCompany(submitData);
                companyId = newCompany.id;
                notifySuccess(t('organizationalStructure.companyBranch.addSuccess'));
            }

            // Reset all states
            await fetchCompanies();
            clearForm();
            setSelectedCompany(null);
            setIsCreating(false);

        } catch (error) {
            console.error('Error saving data:', error);
            notifyError(t('organizationalStructure.companyBranch.saveError'));
        }
    };

    // Function to handle company selection
    const handleCompanySelect = async (company) => {
        setIsCreating(false); // Reset editing mode when selecting a company
        try {
            // Fetch complete company data, signers, and bank accounts for the selected company
            const [companyData, signersData, bankAccountsData] = await Promise.all([
                companiesService.getSelectedCompany(company.id),
                companiesService.getSigners(company.id),
                companiesService.getBankAccounts(company.id)
            ]);

            // Ensure we're working with the correct data structure
            const selectedCompanyData = {
                ...companyData,
                regionId: companyData.regionId?.id?.toString() || '',
                regionName: companyData.regionId?.name || '',
                subregionId: companyData.subregionId?.id?.toString() || '',
                subregionName: companyData.subregionId?.name || '',
                cityId: companyData.cityId || '',
                cityName: companyData.cityId?.name || '',
                tdNraId: typeof companyData.tdNraId === 'object' ? companyData.tdNraId?.id : companyData.tdNraId
            };

            setSelectedCompany(selectedCompanyData);
            const signersMap = signersData.reduce((acc, signer) => {
                acc[signer.mnemonicId] = signer;
                return acc;
            }, {});

            // Create a map of bank accounts by IBAN
            const bankAccountsByIban = bankAccountsData.reduce((acc, account) => {
                acc[account.iban.trim()] = account.id;
                return acc;
            }, {});


            // Helper function to get bank account ID from JSON string
            const getBankAccountId = (jsonStr) => {
                try {
                    const { iban } = JSON.parse(jsonStr);
                    return bankAccountsByIban[iban.trim()]?.toString() || '';
                } catch (e) {
                    return '';
                }
            };

            // Get bank account IDs from the company object
            const bankAccountIds = {
                firmBankAccByFirmaccId: getBankAccountId(JSON.stringify(companyData.firmBankAccByFirmaccId)),
                firmBankAccBySharesFirmaccId: getBankAccountId(JSON.stringify(companyData.firmBankAccBySharesFirmaccId)),
                firmBankAccByDividendFirmaccId: getBankAccountId(JSON.stringify(companyData.firmBankAccByDividendFirmaccId)),
                firmBankAccByCreditTrnsfFirmaccId: getBankAccountId(JSON.stringify(companyData.firmBankAccByCreditTrnsfFirmaccId))
            };


            // Update form data with company and signers data
            const updatedFormData = {
                ...companyData,
                // Use the bank account IDs from the company object
                ...bankAccountIds,
                tdNraId: typeof companyData.tdNraId === 'object' ? companyData.tdNraId?.id : companyData.tdNraId,
                signers: {
                    manager: {
                        position: signersMap['director']?.mnemonicDescription || '',
                        name: signersMap['director']?.qualityDescription || ''
                    },
                    hrManager: {
                        position: signersMap['hrManager']?.mnemonicDescription || '',
                        name: signersMap['hrManager']?.qualityDescription || ''
                    },
                    payrollManager: {
                        position: signersMap['payrollManager']?.mnemonicDescription || '',
                        name: signersMap['payrollManager']?.qualityDescription || ''
                    },
                    chiefAccountant: {
                        position: signersMap['chiefAccountant']?.mnemonicDescription || '',
                        name: signersMap['chiefAccountant']?.qualityDescription || ''
                    },
                    hrChiefExpert: {
                        position: signersMap['hrChiefExpert']?.mnemonicDescription || '',
                        name: signersMap['hrChiefExpert']?.qualityDescription || ''
                    },
                    payrollChiefExpert: {
                        position: signersMap['payrollChiefExpert']?.mnemonicDescription || '',
                        name: signersMap['payrollChiefExpert']?.qualityDescription || ''
                    },
                    cashier: {
                        position: signersMap['cashier']?.mnemonicDescription || '',
                        name: signersMap['cashier']?.qualityDescription || ''
                    }
                }
            };

            setFormData(prev => ({
                ...prev,
                ...updatedFormData
            }));

            // Update bank accounts
            setBankAccounts(Array.isArray(bankAccountsData) ? bankAccountsData : []);

            // Load NKID hierarchy if nkidCode exists
            if (companyData.nkidCode) {
                await loadNkidHierarchy(companyData.nkidCode);
            }
        } catch (error) {
            console.error('Error fetching company data:', error);
            notifyError(t('organizationalStructure.companyBranch.fetchError'));
        }
    };

    // Load NKID hierarchy for selected company
    const loadNkidHierarchy = async (nkidCode) => {
        try {
            const hierarchyData = await companiesService.getNkpdHierarchy(nkidCode);

            if (Array.isArray(hierarchyData) && hierarchyData.length > 0) {
                const hierarchyLevels = hierarchyData.sort((a, b) => a.value.length - b.value.length);

                // Level 1: Class
                if (hierarchyLevels.length >= 1) {
                    const classLevel = hierarchyLevels[0];
                    setFormData(prev => ({ ...prev, nkidLevel1: classLevel.value }));
                    setSelectedCompany(prev => ({ ...prev, nkidLevel1: classLevel.value }));
                }

                // Level 2: Subclass
                if (hierarchyLevels.length >= 2) {
                    const subclassLevel = hierarchyLevels[1];
                    setFormData(prev => ({ ...prev, nkidLevel2: subclassLevel.value }));
                    setSelectedCompany(prev => ({ ...prev, nkidLevel2: subclassLevel.value }));

                    // Load subclass options
                    setNkidDropdownOptions(prev => ({
                        ...prev,
                        level2: { ...prev.level2, loading: true }
                    }));

                    const subclassData = await companiesService.getBusinessQualification(hierarchyLevels[0].value);
                    const subclassOptions = subclassData.map(item => ({
                        value: item.value,
                        label: `${item.value} - ${item.note}`
                    }));

                    setNkidDropdownOptions(prev => ({
                        ...prev,
                        level2: { options: subclassOptions, loading: false }
                    }));
                }

                // Level 3: Group
                if (hierarchyLevels.length >= 3) {
                    const groupLevel = hierarchyLevels[2];
                    setFormData(prev => ({ ...prev, nkidLevel3: groupLevel.value, nkidCode: groupLevel.value }));
                    setSelectedCompany(prev => ({ ...prev, nkidLevel3: groupLevel.value, nkidCode: groupLevel.value }));

                    // Load group options
                    setNkidDropdownOptions(prev => ({
                        ...prev,
                        level3: { ...prev.level3, loading: true }
                    }));

                    const groupData = await companiesService.getBusinessQualification(hierarchyLevels[1].value);
                    const groupOptions = groupData.map(item => ({
                        value: item.value,
                        label: `${item.value} - ${item.note}`
                    }));

                    setNkidDropdownOptions(prev => ({
                        ...prev,
                        level3: { options: groupOptions, loading: false }
                    }));
                }
            }
        } catch (error) {
            console.error('Error loading NKID hierarchy:', error);
        }
    };

    const handleNavigateToBankAccounts = (fieldName) => {
        navigate('/payments/settings/company-bank-accounts', {
            state: {
                selectionMode: true,
                fieldName: fieldName,
                returnPath: location.pathname,
                // Preserve all state
                preservedState: {
                    selectedCompany: selectedCompany,
                    formData: formData,
                    isCreating: isCreating,
                    bankAccounts: bankAccounts,
                    nkidDropdownOptions: nkidDropdownOptions,
                    showMainData: showMainData,
                    showBankAccounts: showBankAccounts,
                    showCompanyBranch: showCompanyBranch,
                    showSigners: showSigners
                }
            }
        });
    };

    const renderBankAccountSelect = (type, value, onChange, fieldName) => {
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <select
                    className="small-select"
                    disabled={true}
                    value={value || ''}
                    onChange={onChange}
                    style={{ flex: 1 }}
                >
                    <option value="">{t('common.select')}</option>
                    {Array.isArray(bankAccounts) && bankAccounts
                        .filter(account => account.active)
                        .map(account => (
                            <option key={account.id} value={account.id.toString()}>
                                {account.iban}
                            </option>
                        ))}
                </select>
                {isCreating && (
                    <button
                        type="button"
                        className="icon-button"
                        onClick={() => handleNavigateToBankAccounts(fieldName)}
                        title={t('common.select')}
                        style={{ padding: '4px 8px', minWidth: 'auto' }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 11l3 3L22 4"></path>
                            <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"></path>
                        </svg>
                    </button>
                )}
            </div>
        );
    };

    const actionButtons = (
        <>
            <ActionButtons
                buttons={[
                    {
                        ...COMMON_BUTTONS.ADD,
                        onClick: () => handleOpenModal('add')
                    },
                    {
                        ...COMMON_BUTTONS.EDIT,
                        onClick: () => handleOpenModal('edit')
                    },
                    {
                        ...COMMON_BUTTONS.DELETE,
                        onClick: () => handleOpenModal('delete')
                    },
                    {
                        ...COMMON_BUTTONS.SAVE,
                        onClick: handleSubmit(onSubmit),
                        disabled: !isCreating,
                        showWhen: 'always'
                    },
                    {
                        ...COMMON_BUTTONS.CANCEL,
                        onClick: () => {
                            setIsCreating(false);
                            clearForm(false);
                        },
                        disabled: !isCreating,
                        showWhen: 'always'
                    },
                    {
                        ...COMMON_BUTTONS.COST_CENTERS,
                        onClick: () => {
                            if (selectedCompany?.id) {
                                navigate(`/organizational-structure/company-centers/${selectedCompany.id}?name=${encodeURIComponent(selectedCompany.name)}&bulstat=${encodeURIComponent(selectedCompany.bulstat)}`);
                            }
                        }
                    }
                ]}
                selectedRecord={selectedCompany}
            />
        </>
    )

    const handleDelete = async () => {
        try {
            if (!selectedCompany?.id) {
                notifyError(t('organizationalStructure.companyBranch.deleteError'));
                return;
            }

            await companiesService.deleteCompany(selectedCompany.id);
            notifySuccess(t('organizationalStructure.companyBranch.deleteSuccess'));
            setShowDeleteConfirm(false);
            setSelectedCompany(null);
            clearForm(); // Clear form after successful deletion
            await fetchCompanies(); // Refresh the list
        } catch (error) {
            console.error('Error deleting company:', error);
            const errorMessage = error.response?.data?.message || 'Failed to delete record';
            notifyError(errorMessage);
        }
    };

    const handleOpenModal = (type) => {
        if (type === 'add') {
            clearForm();
            setIsCreating(true);
            setSelectedCompany(null); // Ensure no company is selected when creating new
            // Set only main data to be collapsed, others expanded
            setShowMainData(true);
            setShowBankAccounts(false);
            setShowCompanyBranch(true);
            setShowSigners(false);
        } else if (type === 'edit') {
            if (selectedCompany) {
                setIsCreating(true);
                setShowBankAccounts(true);
                setShowCompanyBranch(true);
                setShowSigners(true);
                // Set the location values for the form
                setValue('region', selectedCompany.regionId);
                setValue('subregion', selectedCompany.subregionId);
                setValue('cityId', selectedCompany.cityId);
                // Set the required form fields to prevent validation errors
                setValue('branchNo', selectedCompany.branchNo);
                setValue('name', selectedCompany.name);
                setValue('bulstat', selectedCompany.bulstat);
                setValue('city', selectedCompany.city);
                setValue('postcode', selectedCompany.postcode);
            } else {
                notifyError(t('organizationalStructure.companyBranch.selectCompanyError'));
            }
        } else if (type === 'delete') {
            if (selectedCompany) {
                setShowDeleteConfirm(true);
            } else {
                notifyError(t('organizationalStructure.companyBranch.selectCompanyError'));
            }
        }
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <div className='companies-container'>
            <h1 className='title'>{t('organizationalStructure.companyBranch.title')}</h1>
            <div className="action-buttons">
                {actionButtons}
            </div>

            {/* Delete Modal */}
            {showDeleteConfirm && (
                <DeleteModal
                    onClose={() => setShowDeleteConfirm(false)}
                    onConfirm={handleDelete}
                    name={selectedCompany?.name || ''}
                    entityType="record"
                />
            )}

            <table className='companies-table font-size-12'>
                <thead>
                    <tr>
                        <th>{t('organizationalStructure.companyBranch.code')}</th>
                        <th>{t('organizationalStructure.companyBranch.name')}</th>
                        <th>{t('organizationalStructure.companyBranch.vatNumber')}</th>
                        <th>{t('organizationalStructure.companyBranch.city')}</th>
                        <th>{t('organizationalStructure.companyBranch.address')}</th>
                        <th>{t('organizationalStructure.companyBranch.phone')}</th>
                    </tr>
                </thead>
                <tbody>
                    {companiesData.map((company) => (
                        <tr
                            key={company.id}
                            onClick={() => handleCompanySelect(company)}
                            className={selectedCompany?.id === company.id ? 'selected-row' : ''}
                            style={{ cursor: 'pointer' }}
                        >
                            <td>{company.branchNo}</td>
                            <td>{company.name}</td>
                            <td>{company.bulstat}</td>
                            <td>{company.city}</td>
                            <td>{company.address}</td>
                            <td>{company.phone}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <section className='section-container'>
                <div className="first-table">
                    <CollapsibleHeader
                        title={t('organizationalStructure.companyBranch.mainData.mainData')}
                        isOpen={showMainData}
                        onToggle={() => setShowMainData(!showMainData)}
                    />
                    {showMainData && (
                        <div className="collapsible-content expanded">
                            <table className="form-table">
                                <tbody>
                                    <tr>
                                        <td className="form-label">{t('organizationalStructure.companyBranch.mainData.title')} <span className="required">*</span></td>
                                        <td className="form-input" colSpan="3">
                                            <input
                                                className={`small-input ${errors.name ? 'input-error' : ''}`}
                                                type='text'
                                                maxLength={50}
                                                placeholder={
                                                    errors.name
                                                        ? errors.name.message
                                                        : t('common.required')
                                                }
                                                {...register('name')}
                                                value={isCreating ? formData.name : selectedCompany?.name || ''}
                                                onChange={(e) => handleInputChange('name', e.target.value)}
                                                readOnly={!isCreating}
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="form-label">{t('organizationalStructure.companyBranch.mainData.companyCode')} <span className="required">*</span></td>
                                        <td className="form-input">
                                            <input
                                                className={`small-input ${errors.branchNo ? 'input-error' : ''}`}
                                                type='text'
                                                placeholder={
                                                    errors.branchNo
                                                        ? errors.branchNo.message
                                                        : t('common.required')
                                                }
                                                maxLength={10}
                                                {...register('branchNo')}
                                                value={isCreating ? formData.branchNo : selectedCompany?.branchNo || ''}
                                                onChange={(e) => handleInputChange('branchNo', e.target.value)}
                                                readOnly={!isCreating}
                                            />
                                        </td>
                                        <td className="form-label">{t('organizationalStructure.companyBranch.mainData.status')}:</td>
                                        <td className="form-input">
                                            <select
                                                className="small-select"
                                                value={isCreating ? formData.status : selectedCompany?.status}
                                                onChange={(e) => handleInputChange('status', parseInt(e.target.value))}
                                                disabled={!isCreating}
                                            >
                                                <option value={1}>{t('organizationalStructure.companyBranch.mainData.active')}</option>
                                                <option value={0}>{t('organizationalStructure.companyBranch.mainData.inactive')}</option>
                                                <option value={2}>{t('organizationalStructure.companyBranch.mainData.transfered')}</option>
                                            </select>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="form-label">{t('organizationalStructure.companyBranch.mainData.allias')}:</td>
                                        <td className="form-input">
                                            <input
                                                className='small-input'
                                                type='text'
                                                maxLength={10}
                                                value={isCreating ? formData.aliasBranchNo : selectedCompany?.aliasBranchNo || ''}
                                                onChange={(e) => handleInputChange('aliasBranchNo', e.target.value)}
                                                readOnly={!isCreating}
                                            />
                                        </td>
                                        <td className="form-label">{t('organizationalStructure.companyBranch.mainData.city')} <span className="required">*</span></td>
                                        <td className="form-input">
                                            <input
                                                className={`small-input ${errors.city ? 'input-error' : ''}`}
                                                maxLength={50}
                                                type='text'
                                                placeholder={
                                                    errors.city
                                                        ? errors.city.message
                                                        : t('common.required')
                                                }
                                                {...register('city')}
                                                value={isCreating ? formData.city : selectedCompany?.city || ''}
                                                onChange={(e) => handleInputChange('city', e.target.value)}
                                                readOnly={!isCreating}
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="form-label">{t('organizationalStructure.companyBranch.mainData.vatNumber')} <span className="required">*</span></td>
                                        <td className="form-input">
                                            <input
                                                className={`small-input ${errors.bulstat ? 'input-error' : ''}`}
                                                type='text'
                                                placeholder={
                                                    errors.bulstat
                                                        ? errors.bulstat.message
                                                        : t('common.required')
                                                }
                                                {...register('bulstat')}
                                                maxLength={13}
                                                value={isCreating ? formData.bulstat : selectedCompany?.bulstat || ''}
                                                onChange={(e) => handleInputChange('bulstat', e.target.value)}
                                                readOnly={!isCreating}
                                            />
                                        </td>
                                        <td className="form-label">{t('organizationalStructure.companyBranch.mainData.nationalRevenue')}:</td>
                                        <td className="form-input">
                                            <input
                                                className='small-input'
                                                maxLength={100}
                                                type='text'
                                                value={isCreating ? formData.tdNra : selectedCompany?.tdNra || ''}
                                                onChange={(e) => handleInputChange('tdNra', e.target.value)}
                                                readOnly={!isCreating}
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="form-label">{t('organizationalStructure.companyBranch.mainData.accountNationalRevenue')}:</td>
                                        <td className="form-input">
                                            <input
                                                className='small-input'
                                                maxLength={22}
                                                type='text'
                                                value={isCreating ? formData.ibanTdNra : selectedCompany?.ibanTdNra || ''}
                                                onChange={(e) => handleInputChange('ibanTdNra', e.target.value)}
                                                readOnly={!isCreating}
                                            />
                                        </td>
                                        <td className="form-label">{t('organizationalStructure.companyBranch.mainData.representationNationalRevenue')}:</td>
                                        <td className="form-input">
                                            <select
                                                className="small-select"
                                                {...register('tdNraId')}
                                                value={isCreating ? formData.tdNraId : selectedCompany?.tdNraId || ''}
                                                onChange={(e) => handleInputChange('tdNraId', e.target.value)}
                                                disabled={!isCreating}
                                            >
                                                <option value="">{t('common.select')}</option>
                                                {nationalRevenue.map((item) => (
                                                    <option key={item.id} value={item.id}>{item.code} - {item.description}</option>
                                                ))}
                                            </select>
                                        </td>
                                    </tr>


                                    <tr>
                                        <td className="form-label">{t('organizationalStructure.companyBranch.mainData.email')}:</td>
                                        <td className="form-input">
                                            <input
                                                className='small-input'
                                                maxLength={100}
                                                type='text'
                                                value={isCreating ? formData.email : selectedCompany?.email || ''}
                                                onChange={(e) => handleInputChange('email', e.target.value)}
                                                readOnly={!isCreating}
                                            />
                                        </td>
                                        <td className="form-label">{t('organizationalStructure.companyBranch.mainData.address')}:</td>
                                        <td className="form-input">
                                            <input
                                                className='small-input'
                                                maxLength={50}
                                                type='text'
                                                value={isCreating ? formData.address : selectedCompany?.address || ''}
                                                onChange={(e) => handleInputChange('address', e.target.value)}
                                                readOnly={!isCreating}
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="form-label">{t('organizationalStructure.companyBranch.mainData.phone')}:</td>
                                        <td className="form-input">
                                            <input
                                                className='small-input'
                                                maxLength={50}
                                                type='text'
                                                value={isCreating ? formData.phone : selectedCompany?.phone || ''}
                                                onChange={(e) => handleInputChange('phone', e.target.value)}
                                                readOnly={!isCreating}
                                            />
                                        </td>
                                        <td className="form-label">{t('organizationalStructure.companyBranch.mainData.mobile')}:</td>
                                        <td className="form-input">
                                            <input
                                                className='small-input'
                                                maxLength={50}
                                                type='text'
                                                value={isCreating ? formData.mobilephone : selectedCompany?.mobilephone || ''}
                                                onChange={(e) => handleInputChange('mobilephone', e.target.value)}
                                                readOnly={!isCreating}
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="form-label">{t('organizationalStructure.companyBranch.mainData.region',)}:</td>
                                        <td className="form-input" colSpan="3">
                                            <CompanyLocationDropDowns
                                                key={`${selectedCompany?.id || 'new'}-${isCreating ? 'creating' : 'viewing'}-${selectedCompany?.cityId?.id || 'empty'}`}
                                                register={register}
                                                setValue={setValue}
                                                data={selectedCompany}
                                                hideCountry={true}
                                                disabled={!isCreating}
                                                onCityChange={handleCityChange}
                                                additionalFields={
                                                    <>

                                                        <input
                                                            className={`small-input ${errors.postcode ? 'input-error' : ''}`}
                                                            type='text'
                                                            placeholder={
                                                                t('organizationalStructure.companyBranch.mainData.postalCode')
                                                            }
                                                            {...register('postcode')}
                                                            value={isCreating ? formData.postcode : selectedCompany?.postcode || ''}
                                                            onChange={(e) => handleInputChange('postcode', e.target.value)}
                                                            readOnly={true}
                                                            style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                                                        />
                                                    </>
                                                }
                                            />
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <h3 style={{ fontSize: '16px', fontWeight: 500 }}>{t('organizationalStructure.companyBranch.mainData.bussinessClalification')}: </h3>
                            <div style={{ border: '1px solid #ccc', borderRadius: '10px', padding: '10px' }}>
                                <table className="form-table">
                                    <tbody>
                                        <tr>
                                            <td className="form-label">{t('organizationalStructure.companyBranch.mainData.section')}:</td>
                                            <td className="form-input" colSpan="3">
                                                <select
                                                    className='small-select'
                                                    value={isCreating ? formData.nkidLevel1 : selectedCompany?.nkidLevel1 || ''}
                                                    onChange={(e) => handleNkidLevel1Change(e.target.value)}
                                                    disabled={!isCreating || nkidDropdownOptions.level1.loading}
                                                >
                                                    <option value="">
                                                        {nkidDropdownOptions.level1.loading ? 'Зареждане...' : t('common.select')}
                                                    </option>
                                                    {nkidDropdownOptions.level1.options.map((option, index) => (
                                                        <option key={`nkid-level1-${option.value}-${index}`} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="form-label">{t('organizationalStructure.companyBranch.mainData.group')}:</td>
                                            <td className="form-input" colSpan="3">
                                                <select
                                                    className='small-select'
                                                    value={isCreating ? formData.nkidLevel2 : selectedCompany?.nkidLevel2 || ''}
                                                    onChange={(e) => handleNkidLevel2Change(e.target.value)}
                                                    disabled={!isCreating || nkidDropdownOptions.level2.loading || !formData.nkidLevel1}
                                                >
                                                    <option value="">
                                                        {nkidDropdownOptions.level2.loading ? 'Зареждане...' : t('common.select')}
                                                    </option>
                                                    {nkidDropdownOptions.level2.options.map((option, index) => (
                                                        <option key={`nkid-level2-${option.value}-${index}`} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="form-label">{t('organizationalStructure.companyBranch.mainData.class')}:</td>
                                            <td className="form-input" colSpan="3">
                                                <select
                                                    className='small-select'
                                                    value={isCreating ? formData.nkidLevel3 : selectedCompany?.nkidLevel3 || ''}
                                                    onChange={(e) => handleNkidLevel3Change(e.target.value)}
                                                    disabled={!isCreating || nkidDropdownOptions.level3.loading || !formData.nkidLevel2}
                                                >
                                                    <option value="">
                                                        {nkidDropdownOptions.level3.loading ? 'Зареждане...' : t('common.select')}
                                                    </option>
                                                    {nkidDropdownOptions.level3.options.map((option, index) => (
                                                        <option key={`nkid-level3-${option.value}-${index}`} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
                <div className="second-table">
                    {(!isCreating || selectedCompany) && (
                        <div className="bank-accounts-container">
                            <CollapsibleHeader
                                title={t('organizationalStructure.companyBranch.bankAccounts.bankAccounts')}
                                isOpen={showBankAccounts}
                                onToggle={() => setShowBankAccounts(!showBankAccounts)}
                            />
                            <div className={`collapsible-content ${showBankAccounts ? 'expanded' : 'collapsed'}`}>
                                <table className="form-table">
                                    <tbody>
                                        <tr>
                                            <td className="form-label">{t('organizationalStructure.companyBranch.bankAccounts.massPayments')}:</td>
                                            <td className="form-input">
                                                {renderBankAccountSelect(
                                                    'massPayments',
                                                    formData.firmBankAccByFirmaccId,
                                                    (e) => handleInputChange('firmBankAccByFirmaccId', e.target.value),
                                                    'firmBankAccByFirmaccId',
                                                )}

                                            </td>
                                            <td className="form-label">{t('organizationalStructure.companyBranch.bankAccounts.sharesStaff')}:</td>
                                            <td className="form-input">
                                                {renderBankAccountSelect(
                                                    'sharesStaff',
                                                    formData.firmBankAccBySharesFirmaccId,
                                                    (e) => handleInputChange('firmBankAccBySharesFirmaccId', e.target.value),
                                                    'firmBankAccBySharesFirmaccId'
                                                )}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="form-label">{t('organizationalStructure.companyBranch.bankAccounts.divident')}:</td>
                                            <td className="form-input">
                                                {renderBankAccountSelect(
                                                    'dividend',
                                                    formData.firmBankAccByDividendFirmaccId,
                                                    (e) => handleInputChange('firmBankAccByDividendFirmaccId', e.target.value),
                                                    'firmBankAccByDividendFirmaccId'
                                                )}
                                            </td>
                                            <td className="form-label">{t('organizationalStructure.companyBranch.bankAccounts.creditTransfer')}:</td>
                                            <td className="form-input">
                                                {renderBankAccountSelect(
                                                    'creditTransfers',
                                                    formData.firmBankAccByCreditTrnsfFirmaccId,
                                                    (e) => handleInputChange('firmBankAccByCreditTrnsfFirmaccId', e.target.value),
                                                    'firmBankAccByCreditTrnsfFirmaccId'
                                                )}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    <div className="company-branch-container">
                        <CollapsibleHeader
                            title={t('organizationalStructure.companyBranch.formedForCompany.title')}
                            isOpen={showCompanyBranch}
                            onToggle={() => setShowCompanyBranch(!showCompanyBranch)}
                        />
                        {showCompanyBranch && (
                            <table className="form-table">
                                <tbody>
                                    <tr>
                                        <td className="form-label">{t('organizationalStructure.companyBranch.formedForCompany.accountingOperations')}:</td>
                                        <td className="form-input">
                                            <select
                                                className="small-select"
                                                value={isCreating ? formData.formedAccOperations : selectedCompany?.formedAccOperations || 0}
                                                onChange={(e) => handleInputChange('formedAccOperations', parseInt(e.target.value))}
                                                disabled={!isCreating}
                                            >
                                                <option value="1">{t('organizationalStructure.companyBranch.formedForCompany.yes')}</option>
                                                <option value="0">{t('organizationalStructure.companyBranch.formedForCompany.no')}</option>
                                            </select>
                                        </td>
                                        <td className="form-label">{t('organizationalStructure.companyBranch.formedForCompany.paymentDocuments')}:</td>
                                        <td className="form-input">
                                            <select
                                                className="small-select"
                                                value={isCreating ? formData.formedPaymentDocs : selectedCompany?.formedPaymentDocs || 0}
                                                onChange={(e) => handleInputChange('formedPaymentDocs', parseInt(e.target.value))}
                                                disabled={!isCreating}
                                            >
                                                <option value="1">{t('organizationalStructure.companyBranch.formedForCompany.yes')}</option>
                                                <option value="0">{t('organizationalStructure.companyBranch.formedForCompany.no')}</option>
                                            </select>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        )}
                    </div>
                    {(!isCreating || selectedCompany) && (
                        <div className="signers-container">
                            <CollapsibleHeader
                                title={t('organizationalStructure.companyBranch.signers.title')}
                                isOpen={showSigners}
                                onToggle={() => setShowSigners(!showSigners)}
                            />
                            {showSigners && (
                                <div className="collapsible-content expanded">
                                    <table className='form-table'>
                                        <tbody>
                                            <tr>
                                                <td className='form-label'>{t('organizationalStructure.companyBranch.signers.forManagerBranch')}:</td>
                                                <td className='form-input'>
                                                    <input
                                                        className='small-input'
                                                        placeholder={t('organizationalStructure.companyBranch.signers.positions')}
                                                        type="text"
                                                        value={formData.signers.manager.position}
                                                        onChange={(e) => handleSignersInputChange('manager', 'position', e.target.value)}
                                                        readOnly={!isCreating}
                                                    />
                                                </td>
                                                <td className='form-label'></td>
                                                <td className='form-input'>
                                                    <input
                                                        className='small-input'
                                                        placeholder={t('organizationalStructure.companyBranch.signers.name')}
                                                        type="text"
                                                        value={formData.signers.manager.name}
                                                        onChange={(e) => handleSignersInputChange('manager', 'name', e.target.value)}
                                                        readOnly={!isCreating}
                                                    />
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className='form-label'>{t('organizationalStructure.companyBranch.signers.forHrManager')}:</td>
                                                <td className='form-input'>
                                                    <input
                                                        className='small-input'
                                                        placeholder={t('organizationalStructure.companyBranch.signers.positions')}
                                                        type="text"
                                                        value={formData.signers.hrManager.position}
                                                        onChange={(e) => handleSignersInputChange('hrManager', 'position', e.target.value)}
                                                        readOnly={!isCreating}
                                                    />
                                                </td>
                                                <td className='form-label'></td>
                                                <td className='form-input'>
                                                    <input
                                                        className='small-input'
                                                        placeholder={t('organizationalStructure.companyBranch.signers.name')}
                                                        type="text"
                                                        value={formData.signers.hrManager.name}
                                                        onChange={(e) => handleSignersInputChange('hrManager', 'name', e.target.value)}
                                                        readOnly={!isCreating}
                                                    />
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className='form-label'>{t('organizationalStructure.companyBranch.signers.forPayrollManager')}:</td>
                                                <td className='form-input'>
                                                    <input
                                                        className='small-input'
                                                        placeholder={t('organizationalStructure.companyBranch.signers.positions')}
                                                        type="text"
                                                        value={formData.signers.payrollManager.position}
                                                        onChange={(e) => handleSignersInputChange('payrollManager', 'position', e.target.value)}
                                                        readOnly={!isCreating}
                                                    />
                                                </td>
                                                <td className='form-label'></td>
                                                <td className='form-input'>
                                                    <input
                                                        className='small-input'
                                                        placeholder={t('organizationalStructure.companyBranch.signers.name')}
                                                        type="text"
                                                        value={formData.signers.payrollManager.name}
                                                        onChange={(e) => handleSignersInputChange('payrollManager', 'name', e.target.value)}
                                                        readOnly={!isCreating}
                                                    />
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className='form-label'>{t('organizationalStructure.companyBranch.signers.forChiefAccountant')}:</td>
                                                <td className='form-input'>
                                                    <input
                                                        className='small-input'
                                                        placeholder={t('organizationalStructure.companyBranch.signers.positions')}
                                                        type="text"
                                                        value={formData.signers.chiefAccountant.position}
                                                        onChange={(e) => handleSignersInputChange('chiefAccountant', 'position', e.target.value)}
                                                        readOnly={!isCreating}
                                                    />
                                                </td>
                                                <td className='form-label'></td>
                                                <td className='form-input'>
                                                    <input
                                                        className='small-input'
                                                        placeholder={t('organizationalStructure.companyBranch.signers.name')}
                                                        type="text"
                                                        value={formData.signers.chiefAccountant.name}
                                                        onChange={(e) => handleSignersInputChange('chiefAccountant', 'name', e.target.value)}
                                                        readOnly={!isCreating}
                                                    />
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className='form-label'>{t('organizationalStructure.companyBranch.signers.forHrChiefExpert')}:</td>
                                                <td className='form-input'>
                                                    <input
                                                        className='small-input'
                                                        placeholder={t('organizationalStructure.companyBranch.signers.positions')}
                                                        type="text"
                                                        value={formData.signers.hrChiefExpert.position}
                                                        onChange={(e) => handleSignersInputChange('hrChiefExpert', 'position', e.target.value)}
                                                        readOnly={!isCreating}
                                                    />
                                                </td>
                                                <td className='form-label'></td>
                                                <td className='form-input'>
                                                    <input
                                                        className='small-input'
                                                        placeholder={t('organizationalStructure.companyBranch.signers.name')}
                                                        type="text"
                                                        value={formData.signers.hrChiefExpert.name}
                                                        onChange={(e) => handleSignersInputChange('hrChiefExpert', 'name', e.target.value)}
                                                        readOnly={!isCreating}
                                                    />
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className='form-label'>{t('organizationalStructure.companyBranch.signers.forPayrollChiefExpert')}:</td>
                                                <td className='form-input'>
                                                    <input
                                                        className='small-input'
                                                        placeholder={t('organizationalStructure.companyBranch.signers.positions')}
                                                        type="text"
                                                        value={formData.signers.payrollChiefExpert.position}
                                                        onChange={(e) => handleSignersInputChange('payrollChiefExpert', 'position', e.target.value)}
                                                        readOnly={!isCreating}
                                                    />
                                                </td>
                                                <td className='form-label'></td>
                                                <td className='form-input'>
                                                    <input
                                                        className='small-input'
                                                        placeholder={t('organizationalStructure.companyBranch.signers.name')}
                                                        type="text"
                                                        value={formData.signers.payrollChiefExpert.name}
                                                        onChange={(e) => handleSignersInputChange('payrollChiefExpert', 'name', e.target.value)}
                                                        readOnly={!isCreating}
                                                    />
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className='form-label'>{t('organizationalStructure.companyBranch.signers.forCashier')}:</td>
                                                <td className='form-input'>
                                                    <input
                                                        className='small-input'
                                                        placeholder={t('organizationalStructure.companyBranch.signers.positions')}
                                                        type="text"
                                                        value={formData.signers.cashier.position}
                                                        onChange={(e) => handleSignersInputChange('cashier', 'position', e.target.value)}
                                                        readOnly={!isCreating}
                                                    />
                                                </td>
                                                <td className='form-label'></td>
                                                <td className='form-input'>
                                                    <input
                                                        className='small-input'
                                                        placeholder={t('organizationalStructure.companyBranch.signers.name')}
                                                        type="text"
                                                        value={formData.signers.cashier.name}
                                                        onChange={(e) => handleSignersInputChange('cashier', 'name', e.target.value)}
                                                        readOnly={!isCreating}
                                                    />
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </section >
        </div >
    )
}
